/**
 * UploadStep — etapa 1 de "Importar base de clientes" (Item 391-C).
 *
 * Lê um arquivo .csv via FileReader, parseia client-side (`parseCsv`) e devolve
 * `{ headers, rows }` ao caller. Mostra o nome do arquivo + nº de linhas
 * detectadas, ou um erro legível de parse.
 */

import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { parseCsv } from './csv-parser';

import type { ParsedCsv } from './csv-parser';
import type { ChangeEvent } from 'react';

export interface UploadStepProps {
  fileName: string | null;
  parsed: ParsedCsv | null;
  onParsed: (fileName: string, parsed: ParsedCsv) => void;
}

export function UploadStep({ fileName, parsed, onParsed }: UploadStepProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file === undefined) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = (): void => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      try {
        const result = parseCsv(text);
        onParsed(file.name, result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível ler o arquivo.');
      }
    };
    reader.onerror = (): void => setError('Falha ao ler o arquivo.');
    reader.readAsText(file, 'utf-8');
  };

  return (
    <section className="cr-step" aria-labelledby="cr-upload-title">
      <h2 id="cr-upload-title" className="cr-step__title">
        <span className="cr-step__num">1</span>
        Carregar arquivo
      </h2>

      <div className="cr-upload__drop">
        <Upload size={28} aria-hidden />
        <p className="cr-upload__hint">
          Selecione um arquivo <strong>.csv</strong> com a base do seu tenant. A primeira linha deve
          conter os nomes das colunas.
        </p>
        <button
          type="button"
          className="cr-btn cr-btn--ghost"
          onClick={() => inputRef.current?.click()}
        >
          Escolher arquivo
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleChange}
          data-testid="cr-file-input"
        />
      </div>

      {fileName !== null && (
        <p className="cr-upload__filename" data-testid="cr-filename">
          {fileName}
        </p>
      )}
      {parsed !== null && (
        <p className="cr-upload__count" data-testid="cr-rowcount">
          {parsed.rows.length} linha(s) detectada(s) · {parsed.headers.length} coluna(s)
        </p>
      )}
      {error !== null && (
        <p className="cr-upload__error" role="alert" data-testid="cr-upload-error">
          {error}
        </p>
      )}
    </section>
  );
}
